var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { MediaContext } from '..';
import { Text, TokenTextAppearance } from '../../text';
import { styled } from '../../theme-provider';
const StyledTimestamp = styled.div.withConfig({ displayName: "StyledTimestamp", componentId: "sc-hu75hv" }) `position:absolute;left:50%;transform:translateX(-50%)translateY(-100%)scale(0.7);opacity:${({ active }) => (active ? 1 : 0)};`;
const StyledIndicator = styled.div.withConfig({ displayName: "StyledIndicator", componentId: "sc-1hrvd8v" }) `position:absolute;opacity:0;left:min(100%, max(0%, var(--progressPercent)));width:${(props) => props.theme.size.static200};height:${(props) => props.theme.size.static200};border-radius:50%;background:${(props) => props.theme.colors.button.primary.active};transform:translate(-50%, -50%)scale(${({ active }) => (active ? 1.3 : 1)});transform-origin:center center;backface-visibility:hidden;transition:opacity 0.2s ease-in-out;`;
const StyledTimeline = styled.div.attrs(({ progress, hoverPosition }) => ({
    style: {
        '--progress': `min(100, max(0, ${progress}))`,
        '--progressPercent': `min(100%, max(0%, ${progress}%))`,
        '--hoverPosition': `min(100, max(0, ${hoverPosition}))`,
    },
})).withConfig({ displayName: "StyledTimeline", componentId: "sc-zdu0df" }) `position:relative;height:${(props) => props.theme.size.line.active};width:100%;background:${(props) => props.theme.colors.button.primary.default};::before,::after{content:'';position:absolute;top:0;left:0;display:block;background:${(props) => props.theme.colors.button.primary.active};height:100%;width:100%;pointer-events:none;transform-origin:left;}::before{transform:scaleX(calc(var(--progress) / 100));}::after{opacity:0;transform:scaleX(calc(var(--hoverPosition) / 100));}&:focus{${StyledIndicator}{opacity:1;}}`;
const StyledTimelineWrapper = styled.div.withConfig({ displayName: "StyledTimelineWrapper", componentId: "sc-1n2axwl" }) `display:flex;width:100%;height:100%;align-items:center;cursor:pointer;:hover{${StyledIndicator}{opacity:1;}${StyledTimeline}{::after{opacity:1;}}}`;
export const Timeline = (_a) => {
    var { 'aria-label': ariaLabel } = _a, rest = __rest(_a, ['aria-label']);
    const timelineWrapperRef = React.useRef(null);
    const mediaElement = React.useContext(MediaContext);
    const [timeStamp, setTimeStamp] = React.useState(0);
    const [progress, setProgress] = React.useState(0);
    const [hoverPosition, setHoverPosition] = React.useState(0);
    const [active, setActive] = React.useState(false);
    const handleTimeupdate = React.useCallback(() => {
        setTimeStamp((mediaElement === null || mediaElement === void 0 ? void 0 : mediaElement.currentTime) || 0);
    }, [mediaElement]);
    React.useEffect(() => {
        mediaElement === null || mediaElement === void 0 ? void 0 : mediaElement.addEventListener('timeupdate', handleTimeupdate);
        return () => {
            mediaElement === null || mediaElement === void 0 ? void 0 : mediaElement.removeEventListener('timeupdate', handleTimeupdate);
        };
    }, [handleTimeupdate, mediaElement]);
    React.useEffect(() => {
        setProgress(((mediaElement === null || mediaElement === void 0 ? void 0 : mediaElement.duration) &&
            timeStamp &&
            (timeStamp / (mediaElement === null || mediaElement === void 0 ? void 0 : mediaElement.duration)) * 100) ||
            0);
    }, [mediaElement, timeStamp]);
    const handleClick = React.useCallback((event) => {
        var _a, _b;
        const left = ((_a = timelineWrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().left) || 0;
        const width = ((_b = timelineWrapperRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().width) || 0;
        if (mediaElement) {
            event.preventDefault();
            event.stopPropagation();
            mediaElement.currentTime =
                ((event.clientX - left) / width) * mediaElement.duration;
        }
    }, [mediaElement]);
    const handleHover = React.useCallback((e) => {
        var _a, _b;
        const left = ((_a = timelineWrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().left) || 0;
        const width = ((_b = timelineWrapperRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().width) || 0;
        setHoverPosition(((e.clientX - left) / width) * 100);
        // Detect if the left mouse button is pressed
        if (mediaElement && e.buttons === 1) {
            setProgress(((e.clientX - left) / width) * 100);
            mediaElement.currentTime =
                ((e.clientX - left) / width) * mediaElement.duration;
            setTimeStamp(((e.clientX - left) / width) * mediaElement.duration);
        }
    }, [mediaElement]);
    const handleTouchMove = React.useCallback((e) => {
        var _a, _b;
        if (!mediaElement || mediaElement.seekable.length === 0) {
            return;
        }
        const left = ((_a = timelineWrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().left) || 0;
        const width = ((_b = timelineWrapperRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().width) || 0;
        setHoverPosition(((e.targetTouches[0].clientX - left) / width) * 100);
        setProgress(((e.targetTouches[0].clientX - left) / width) * 100);
        if (mediaElement) {
            mediaElement.currentTime =
                ((e.targetTouches[0].clientX - left) / width) * mediaElement.duration;
            setTimeStamp(((e.targetTouches[0].clientX - left) / width) * mediaElement.duration);
        }
    }, [mediaElement]);
    const handleClickStart = React.useCallback(() => {
        if (mediaElement && mediaElement.seekable.length === 0) {
            mediaElement.load();
        }
        setActive(true);
    }, [mediaElement]);
    const handleClickEnd = React.useCallback(() => {
        setActive(false);
    }, []);
    const handleKeyDown = React.useCallback((event) => {
        if (mediaElement) {
            const multiplier = event.shiftKey ? 0.05 : 0.01;
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                mediaElement.currentTime -= mediaElement.duration * multiplier;
                setProgress((mediaElement.currentTime / mediaElement.duration) * 100);
            }
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                mediaElement.currentTime += mediaElement.duration * multiplier;
                setProgress((mediaElement.currentTime / mediaElement.duration) * 100);
            }
            if (event.key === 'Home') {
                event.preventDefault();
                mediaElement.currentTime = 0;
                setProgress(0);
            }
            if (event.key === 'End') {
                event.preventDefault();
                mediaElement.currentTime = mediaElement.duration;
                setProgress(100);
            }
        }
    }, [mediaElement]);
    const timeString = React.useMemo(() => {
        const roundedTimeStamp = Math.round(Math.max(0, Math.min((mediaElement === null || mediaElement === void 0 ? void 0 : mediaElement.duration) || 0, timeStamp)));
        const minutes = `${Math.floor(roundedTimeStamp / 60)}`.padStart(2, '0');
        const seconds = `${roundedTimeStamp % 60}`.padStart(2, '0');
        return `${minutes}:${seconds}`;
    }, [timeStamp, mediaElement]);
    return (React.createElement(StyledTimelineWrapper, { onClick: handleClick, onMouseMove: handleHover, onTouchMove: handleTouchMove, onTouchStart: handleClickStart, onTouchEnd: handleClickEnd, onMouseDown: handleClickStart, onMouseUp: handleClickEnd, ref: timelineWrapperRef },
        React.createElement(StyledTimeline, Object.assign({ role: "slider", "aria-valuemin": 0, "aria-valuemax": (mediaElement === null || mediaElement === void 0 ? void 0 : mediaElement.duration) || 0, "aria-valuenow": timeStamp, "aria-valuetext": timeString, progress: progress, "aria-label": ariaLabel ? ariaLabel : 'video timeline', hoverPosition: hoverPosition, tabIndex: 0, onKeyDown: handleKeyDown }, rest),
            React.createElement(StyledIndicator, { active: active },
                React.createElement(StyledTimestamp, { active: active },
                    React.createElement(Text, { appearance: TokenTextAppearance.label100 }, timeString))))));
};
Timeline.displayName = 'Timeline';
